import { useParams } from "react-router";
import { Badge } from "../../../components/shared/Badge";
import { EnterprisePageShell } from "./EnterprisePageShell";
import { usePlatformEnterpriseRoles } from "@platform/hooks";

interface EntRole {
  id: string; name: string; description: string | null;
  type: "system" | "custom"; moduleName: string | null;
  moduleKey: string | null; userCount: number;
}

export function PlatformEnterpriseRolesPage() {
  const { id: enterpriseId } = useParams<{ id: string }>();
  const { data: roles = [], isLoading } = usePlatformEnterpriseRoles(enterpriseId ?? null);
  const typedRoles = roles as EntRole[];

  return (
    <EnterprisePageShell enterpriseId={enterpriseId ?? ""} pageName="Roles">
      <div className="bg-white border border-ds-border rounded-card shadow-card overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-sm text-ds-text-muted">Loading roles…</div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-ds-border bg-ds-bg">
                <th className="text-left text-xs font-medium text-ds-text-muted uppercase tracking-wide px-5 py-3">Role</th>
                <th className="text-left text-xs font-medium text-ds-text-muted uppercase tracking-wide px-5 py-3">Module</th>
                <th className="text-left text-xs font-medium text-ds-text-muted uppercase tracking-wide px-5 py-3">Users in enterprise</th>
                <th className="text-left text-xs font-medium text-ds-text-muted uppercase tracking-wide px-5 py-3">Type</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ds-border">
              {typedRoles.map((r) => (
                <tr key={r.id} className="hover:bg-ds-bg/50">
                  <td className="px-5 py-3.5">
                    <p className="text-sm font-medium text-ds-text">{r.name}</p>
                    {r.description && (
                      <p className="text-xs text-ds-text-muted mt-0.5">{r.description}</p>
                    )}
                  </td>
                  <td className="px-5 py-3.5">
                    {r.moduleName
                      ? <Badge variant="blue">{r.moduleName}</Badge>
                      : <span className="text-xs text-ds-text-muted">All modules</span>}
                  </td>
                  <td className="px-5 py-3.5 text-sm text-ds-text">{r.userCount}</td>
                  <td className="px-5 py-3.5">
                    <Badge variant={r.type === "system" ? "gray" : "purple"}>
                      {r.type === "system" ? "System" : "Custom"}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <p className="text-xs text-ds-text-muted">
        Roles are defined in the platform role builder. Enterprise admins assign them to users.
      </p>
    </EnterprisePageShell>
  );
}
